import { MAPS, type MapDef } from "@neon-purge/shared";
import { el } from "./dom.js";
import { drawMapPreview } from "./mapPreview.js";

/**
 * DESKTOP "damier": a left-side grid of clickable map squares. On desktop it
 * is THE map picker for both the play screen (local pick) and the room lobby
 * (server pick, owner-only): the 3D preview's ‹ › arrows are hidden while the
 * grid is on screen (CSS `.has-damier`) so the same choice is not offered
 * twice. The arrows stay in the DOM because the pad d-pad cycles maps by
 * clicking them, and they come back below 980 px / on touch where this grid
 * is hidden. One square per map valid for the current mode (thumbnail via the
 * cheap 2D `drawMapPreview`, no WebGL per square), plus a trailing 🎲 random
 * square and an optional 🎨 community-maps square.
 */

const RANDOM_ID = "__random__";

export interface MapGridHandle {
  root: HTMLElement;
  /** Re-filter + rebuild squares for a new mode. */
  setMode(mode: string): void;
  /** Highlight the square for the active map id (or __random__). */
  setActive(mapId: string): void;
  /** Read-only (lobby guest): squares stay visible but are disabled. */
  setReadonly(ro: boolean): void;
}

export function mapGrid(opts: {
  mode: string;
  activeId: string;
  /** false → read-only (non-owner in the lobby): no click, dimmed. */
  interactive?: boolean;
  onPick: (mapId: string) => void;
  /** If given, a "🎨 Custom" square is added after 🎲 Random that opens the
   *  community published-maps browser instead of selecting a built-in map. */
  onCustom?: () => void;
}): MapGridHandle {
  const grid = el("div", { class: "map-grid" });
  const root = el("div", { class: "card map-grid-card" },
    el("div", { class: "card-title", text: "CHOOSE A MAP" }), grid);
  let mode = opts.mode;
  let activeId = opts.activeId;
  let readonly = opts.interactive === false;
  root.classList.toggle("readonly", readonly);
  const cells = new Map<string, HTMLButtonElement>();

  // real <button>s (they were clickable <div>s): reachable by Tab, Enter and
  // the pad ring, which is what lets this grid be the ONE desktop map picker
  const cell = (id: string, name: string, m: MapDef | null): HTMLButtonElement => {
    const label = name.replace("🎲 ", "");
    const thumb = m
      ? (() => {
          const cv = document.createElement("canvas");
          cv.width = 128; cv.height = 96; cv.className = "map-cell-thumb";
          drawMapPreview(cv, m);
          return cv;
        })()
      : el("span", { class: "map-cell-thumb map-cell-random", text: "🎲" });
    const c = el("button", { class: "map-cell clickable", type: "button", title: label, "aria-label": `Map: ${label}` },
      thumb, el("span", { class: "map-cell-name", text: label }));
    c.disabled = readonly;
    c.onclick = () => { if (!readonly) opts.onPick(id); };
    cells.set(id, c);
    return c;
  };

  const highlight = (): void => {
    for (const [id, c] of cells) {
      c.classList.toggle("active", id === activeId);
      c.setAttribute("aria-pressed", String(id === activeId));
    }
  };

  const build = (): void => {
    cells.clear();
    const squares: HTMLButtonElement[] = Object.values(MAPS)
      .filter((m) => m.modes.includes(mode))
      // Mega Chibre pinned LAST among the maps, so it sits right before 🎲 Random.
      .sort((a, b) => (a.id === "mega_chibre" ? 1 : b.id === "mega_chibre" ? -1 : 0))
      .map((m) => cell(m.id, m.name, m));
    squares.push(cell(RANDOM_ID, "🎲 Random", null));
    if (opts.onCustom) {
      const c = el("button", { class: "map-cell clickable", type: "button", title: "Community maps", "aria-label": "Community maps" },
        el("span", { class: "map-cell-thumb map-cell-random", text: "🎨" }),
        el("span", { class: "map-cell-name", text: "Custom" }));
      c.onclick = () => opts.onCustom!();
      squares.push(c);
    }
    grid.replaceChildren(...squares);
    highlight();
  };

  build();
  return {
    root,
    setMode(m: string): void { if (m !== mode) { mode = m; build(); } },
    setActive(id: string): void { activeId = id; highlight(); },
    setReadonly(ro: boolean): void {
      if (ro === readonly) return;
      readonly = ro;
      root.classList.toggle("readonly", ro);
      for (const c of cells.values()) c.disabled = ro;
    },
  };
}
